import { useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { SERVICE_CATEGORIES, isCatalogService, testIdFor } from "@/lib/serviceCatalog";

const Chip = ({ name, on, onToggle }) => (
  <button type="button" onClick={onToggle} aria-pressed={on} data-testid={`service-chip-${testIdFor(name)}`}
    className={`inline-flex h-11 items-center gap-1.5 rounded-full border-2 px-3.5 text-sm font-semibold transition-colors duration-150 active:scale-[0.97] ${
      on ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-border bg-card text-foreground"}`}>
    {on && <Check className="h-4 w-4" strokeWidth={3} />}{name}
  </button>
);

const CustomServices = ({ customs, onAdd, onRemove }) => {
  const [draft, setDraft] = useState("");
  const add = () => { if (onAdd(draft)) setDraft(""); };
  return (
    <div data-testid="custom-services">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your custom services</p>
      {customs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {customs.map((c) => (
            <span key={c} className="inline-flex h-11 items-center gap-1 rounded-full bg-[color:var(--brand)] pl-3.5 pr-1.5 text-sm font-semibold text-white" data-testid={`custom-service-${testIdFor(c)}`}>
              {c}
              <button type="button" onClick={() => onRemove(c)} aria-label={`Remove ${c}`} data-testid={`custom-service-remove-${testIdFor(c)}`}
                className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-card/20"><X className="h-4 w-4" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={40} placeholder="e.g. Chimney Repair"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="custom-service-input"
          className="h-12 min-w-0 flex-1 rounded-xl border-2 border-border bg-card px-3 text-[15px] outline-none focus:border-[color:var(--brand)]" />
        <button type="button" onClick={add} data-testid="custom-service-add-button"
          className="flex h-12 items-center gap-1 rounded-xl bg-slate-900 px-4 font-semibold text-white"><Plus className="h-4 w-4" />Add</button>
      </div>
    </div>
  );
};

export const ServicePicker = ({ services, onChange }) => {
  const toggle = (name) => onChange(services.includes(name) ? services.filter((s) => s !== name) : [...services, name]);
  const customs = services.filter((s) => !isCatalogService(s));
  const addCustom = (raw) => {
    const name = raw.trim().replace(/\s+/g, " ");
    if (name.length < 2 || services.some((s) => s.toLowerCase() === name.toLowerCase())) return false;
    onChange([...services, name]);
    return true;
  };
  return (
    <div className="space-y-5" data-testid="service-picker">
      {SERVICE_CATEGORIES.map(({ name, icon: Icon, services: items }) => (
        <div key={name} data-testid={`service-category-${testIdFor(name)}`}>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground"><Icon className="h-4 w-4" />{name}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {items.map((s) => <Chip key={s.name} name={s.name} on={services.includes(s.name)} onToggle={() => toggle(s.name)} />)}
          </div>
        </div>
      ))}
      <CustomServices customs={customs} onAdd={addCustom} onRemove={(c) => onChange(services.filter((s) => s !== c))} />
      <p className="text-sm text-muted-foreground" data-testid="service-picker-count">{services.length} selected · only these show on your quote form</p>
    </div>
  );
};
